import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { OrdersService, AdminOrder, OrderStatus, AdminOrderItemViewModel } from '../../../services/orders/orders.service';
import { SnackbarService } from '../../../services/design/snackbar.service';
import { RiderService, Rider } from '../../../services/riders/rider.service';
import { FormsModule } from '@angular/forms';
import { IstDatePipe } from '../../../pipes/ist-date.pipe';
import { PickingSlipService } from '../../../services/orders/picking-slip.service';
import { OrderBillSummaryComponent } from '../components/order-bill-summary.component';
import { PackingTaskComponent } from '../../operations/packing-task.component';
import { DeliveryReturnReviewComponent } from '../components/delivery-return-review.component';
import { AuthService } from '../../../services/auth/auth.service';
import { firstValueFrom } from 'rxjs';
import { orderDeliveryMapUrl } from '../../../utils/order-delivery-map';

@Component({
    selector: 'app-order-detail',
    standalone: true,
    imports: [CommonModule, RouterModule, FormsModule, IstDatePipe, OrderBillSummaryComponent, PackingTaskComponent, DeliveryReturnReviewComponent],
    template: `
    <div class="space-y-6 pb-20">
      <!-- Top Action Bar -->
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-4">
          <button class="btn btn-ghost btn-circle" routerLink="/orders">
            <i class="fa-solid fa-arrow-left"></i>
          </button>
          <div>
            <h2 class="text-2xl font-black flex items-center gap-3">
              {{ order()?.order_number || 'Order #' + order()?.order_id }}
              @if (order()?.order_source === 'ADMIN_ASSISTED') {
                 <span class="badge badge-primary font-black border-none text-[10px] uppercase">Assisted</span>
              }
            </h2>
            <p class="text-[10px] opacity-40 font-black uppercase tracking-widest">{{ order()?.created_at | istDate:'medium' }}</p>
            @if (order()) {
              <div class="mt-3 flex flex-wrap items-center gap-2">
                <span class="rounded-lg border border-primary/20 bg-primary/10 px-3 py-2 text-sm font-bold">Payment mode: {{ paymentModeLabel() }}</span>
                <span class="rounded-lg border border-base-300 px-3 py-2 text-sm">Payment status: {{ order()?.payment_status || 'Not recorded' }}</span>
              </div>
            }
          </div>
        </div>
        <div class="flex gap-2">
           <button class="btn btn-outline btn-sm rounded-xl">
              <i class="fa-solid fa-print opacity-40"></i>
           </button>
           @if (order()?.current_status !== 'CANCELLED' && order()?.current_status !== 'DELIVERED') {
             @if (order()?.current_status === 'DISPATCHED' || order()?.current_status === 'RIDER_NEAR_CUSTOMER') {
               <button class="btn btn-warning btn-sm rounded-xl text-white" (click)="showReportFailureModal = true">
                  REPORT DELIVERY FAILURE
               </button>
             } @else {
               <button class="btn btn-error btn-sm rounded-xl text-white" (click)="showCancelModal = true">
                  CANCEL
               </button>
             }
           }
           @if (order()?.current_status === 'CANCELLED' && $any(order())?.return_status === 'RETURN_PENDING') {
               <button class="btn btn-info btn-sm rounded-xl text-white" (click)="showReturnModal = true">
                  CONFIRM RETURN
               </button>
           }
        </div>
      </div>

      <!-- Quick Action Commands -->
      @if (order(); as current) { @if (authService.role() === 'superadmin' || authService.role() === 'store_manager') { <app-delivery-return-review [orderId]="current.order_id" [items]="current.items" (changed)="loadOrder(current.order_id)"></app-delivery-return-review> } }
      @if (order()?.current_status !== 'CANCELLED' && order()?.current_status !== 'DELIVERED') {
        <div class="bg-primary/5 border border-primary/10 rounded-2xl p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
           <div class="flex items-center gap-4">
              <div class="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center animate-pulse">
                 <i class="fa-solid fa-bolt"></i>
              </div>
              <div>
                 <p class="text-xs font-black uppercase tracking-widest opacity-40">Next Action Required</p>
                 <p class="text-lg font-black text-primary">{{ order()?.action_required }}</p>
              </div>
           </div>
           <div class="flex gap-2">
              @if (order()?.current_status === 'PENDING') {
                <button class="btn btn-primary rounded-xl" (click)="updateStatus('CONFIRMED')">Confirm Order Now</button>
              }
              @if (canCompleteOrder()) {
                <button class="btn btn-success rounded-xl text-white" [disabled]="completing()" (click)="completeOrder()">{{ order()?.rider_id ? 'Complete Delivery' : 'Customer Collected · Complete Order' }}</button>
              }
           </div>
        </div>
      }

      @if (canCompleteOrder()) {
        <p class="text-sm opacity-70">Customer received the order? Confirm the items and payment to complete it.</p>
      }
      @if (order(); as current) {
        @if (current.current_status !== 'CANCELLED' && current.current_status !== 'DELIVERED' && current.current_status !== 'DISPATCHED' && current.current_status !== 'RIDER_NEAR_CUSTOMER') {
          <app-packing-task [orderId]="current.order_id" (changed)="loadOrder(current.order_id)" />
        }
      }
      @if (showCompleteModal) {
        <div class="modal modal-open" role="dialog" aria-modal="true" aria-labelledby="complete-order-title">
          <div class="modal-box space-y-4">
            <h3 id="complete-order-title" class="text-lg font-bold">Complete Order #{{ order()?.order_id }}</h3>
            <p class="font-semibold">{{ completionMode === 'STORE_COLLECTION' ? 'Customer collected from store' : 'Delivered by assigned rider' }}</p>
            <div class="flex items-center justify-between rounded-xl border border-base-200 px-4 py-3">
              <span class="text-sm opacity-60">Payment mode</span>
              <strong>{{ paymentModeLabel() }}</strong>
            </div>
            <div class="rounded-xl bg-base-200/50 p-4">
              <p class="mb-2 text-xs font-semibold uppercase opacity-60">Items to confirm</p>
              @for (item of completionItems(); track item.id) {
                <div class="flex items-center justify-between gap-3 py-2 text-sm">
                  <span>{{ item.product_name }} <span class="opacity-60">{{ item.variant_name }}</span></span>
                  <span class="whitespace-nowrap font-bold">× {{ item.fulfilled_quantity }}</span>
                </div>
              }
            </div>
            <label class="flex items-start gap-3 rounded-xl border border-base-300 p-4 cursor-pointer">
              <input type="checkbox" class="checkbox checkbox-success" [(ngModel)]="completionReceived" [disabled]="completing()" />
              <span>I checked all the items and quantities above. The customer has received them.</span>
            </label>
            <p class="text-xs opacity-60">Something missing? Cancel this form and adjust the order first.</p>
            @if (completionMode === 'RIDER_DELIVERY') {
              <label class="flex items-start gap-3 rounded-xl border border-base-300 p-4"><input type="checkbox" class="checkbox" [(ngModel)]="completionHandover" [disabled]="completing()" />Items were handed to the assigned rider.</label>
            }
            @if (order()?.payment_status?.toUpperCase() === 'PAID') {
              <div class="rounded-xl bg-success/10 p-4 text-success font-semibold">✓ Payment received</div>
            } @else if (canConfirmCompletionCash()) {
              <label class="flex gap-3 items-start rounded-xl bg-base-200/50 p-4">
                <input type="checkbox" class="checkbox" [(ngModel)]="completionCashConfirmed" [disabled]="completing()" />
                <span>₹{{ order()?.payable_amount ?? order()?.total_amount }} cash received by {{ completionMode === 'STORE_COLLECTION' ? 'the store' : 'the rider' }}.</span>
              </label>
            } @else {
              <p class="text-error">Online payment must be verified before completing this order. Refresh after payment confirmation.</p>
            }
            <details class="text-sm">
              <summary class="cursor-pointer opacity-60">Add a note (optional)</summary>
              <textarea aria-label="Completion note" class="textarea textarea-bordered w-full mt-2" [(ngModel)]="completionReason" [disabled]="completing()" maxlength="800"></textarea>
            </details>
            <div class="modal-action">
              <button class="btn" [disabled]="completing()" (click)="showCompleteModal = false">Cancel</button>
              <button class="btn btn-success" [disabled]="!canSubmitCompletion()" (click)="submitCompletion()">{{ completing() ? 'Completing…' : 'Complete Order' }}</button>
            </div>
          </div>
        </div>
      }

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Center/Left: Items & Content -->
        <div class="lg:col-span-2 space-y-6">

           <!-- Print Status Card -->
           @if (order()?.picking_slip_print_triggered_at) {
             <div class="card bg-base-100 border border-base-200 shadow-sm overflow-hidden mb-6">
                <div class="px-6 py-4 flex flex-wrap items-center justify-between gap-4">
                   <div>
                       <h3 class="font-black text-[12px] uppercase tracking-widest opacity-60">Picking Slip</h3>
                       <p class="text-xs font-bold mt-1 text-success">Triggered at {{ order()?.picking_slip_print_triggered_at | istDate:'shortTime' }}</p>
                       <p class="text-[10px] font-bold opacity-40">Print count: {{ order()?.picking_slip_trigger_count || 1 }}</p>
                   </div>
                   <button class="btn btn-sm btn-outline rounded-xl" (click)="reprintPickingSlip()" [disabled]="printing()">
                       <i class="fa-solid fa-print opacity-60 mr-2"></i> {{ printing() ? 'Printing...' : 'Reprint Picking Slip' }}
                   </button>
                </div>
             </div>
           } @else if (order()?.current_status === 'PREPARING') {
             <div class="card bg-base-100 border border-base-200 shadow-sm overflow-hidden mb-6">
                <div class="px-6 py-4 flex flex-wrap items-center justify-between gap-4">
                   <div>
                       <h3 class="font-black text-[12px] uppercase tracking-widest opacity-60">Picking Slip</h3>
                       <p class="text-xs font-bold mt-1 opacity-50">
                         @if (order()?.picking_slip_print_triggered_at) {
                           @if (order()?.picking_slip_printed_revision !== order()?.fulfillment_revision) {
                             <span class="text-warning font-black"><i class="fa-solid fa-triangle-exclamation mr-1"></i>REPRINT REQUIRED</span> (Slip out of date)
                           } @else {
                             Printed {{ order()?.picking_slip_print_triggered_at | istDate:'short' }}
                           }
                         } @else {
                           Not printed
                         }
                       </p>
                   </div>
                   <button class="btn btn-sm btn-outline rounded-xl" (click)="reprintPickingSlip()" [disabled]="printing()">
                       <i class="fa-solid fa-print opacity-60 mr-2"></i> {{ printing() ? 'Printing...' : (order()?.picking_slip_print_triggered_at && order()?.picking_slip_printed_revision !== order()?.fulfillment_revision ? 'Reprint Corrected Slip' : 'Print Picking Slip') }}
                   </button>
                </div>
             </div>
           }

           <div class="card bg-base-100 border border-base-200 shadow-sm overflow-hidden">
              <div class="px-6 py-4 border-b border-base-200 bg-base-100 flex justify-between items-center">
                  <h3 class="font-black text-sm uppercase tracking-widest opacity-60">Order Breakdown</h3>
                  <span class="badge badge-sm font-black bg-base-200 border-base-300 text-[10px] px-3">Original Lines: {{ order()?.summary?.items || 0 }} | Active Lines: {{ getActiveLinesCount() }} | Fulfilled Qty: {{ getFulfilledQty() }}</span>
               </div>
               <div class="divide-y divide-base-200">
                  @for (item of order()?.items; track item.variant_sku) {
                      <div class="p-6 flex items-start gap-4 hover:bg-base-200/5 transition" [class.opacity-40]="item.is_fully_removed">
                        <div class="w-14 h-14 bg-base-200 rounded-2xl flex items-center justify-center border border-base-300 relative overflow-hidden">
                           @if (item.image_url) {
                              <img [src]="item.image_url" class="w-full h-full object-cover">
                           } @else {
                              <i class="fa-solid fa-box text-base-content/20 text-xl"></i>
                           }
                           @if (item.is_fully_removed) {
                              <div class="absolute inset-0 bg-base-100/70 border-2 border-error text-error text-[10px] font-black w-full h-full flex flex-col items-center justify-center text-center leading-none">
                                  <span>REMOVED</span>
                              </div>
                           }
                        </div>
                        <div class="flex-1">
                           <p class="text-sm font-black leading-tight" [class.line-through]="item.is_fully_removed">
                              {{ item.product_name }}
                           </p>
                           <div class="flex gap-2 mt-2">
                              <span class="text-[10px] font-mono bg-base-200 px-2 py-0.5 rounded opacity-60">SKU: {{ item.variant_sku || item.sku }}</span>
                              <span class="text-[10px] bg-primary/10 text-primary font-bold px-2 py-0.5 rounded uppercase">{{ item.variant_name }}</span>
                              <span class="text-[10px] bg-base-200 font-bold px-2 py-0.5 rounded uppercase">Ordered {{ item.ordered_quantity }}</span>
                              @if (item.is_partially_removed || item.is_fully_removed) {
                                  <span class="text-[10px] font-bold px-2 py-0.5 rounded uppercase" [ngClass]="item.is_fully_removed ? 'bg-error/10 text-error' : 'bg-warning/10 text-warning'">Removed {{ item.removed_quantity }} | Fulfilled {{ item.fulfilled_quantity }}</span>
                              }
                           </div>
                        </div>
                        <div class="text-right">
                           <p class="text-xs opacity-40 font-bold line-through">Rs {{ item.original_line_total | number:'1.0-0' }}</p>
                           <p class="text-sm font-black mt-1" [class.text-error]="item.is_fully_removed" [class.text-primary]="!item.is_fully_removed">Rs {{ item.fulfilled_line_total | number:'1.0-0' }}</p>
                           @if (canAdjustFulfillment() && !item.is_fully_removed) {
                              <button class="btn btn-xs mt-2 btn-warning btn-outline" (click)="openAdjustModal(item)">Adjust Fulfillment</button>
                           }
                        </div>
                     </div>
                  }
              </div>
              <!-- Grand Total Section -->
              <div class="p-6 bg-base-200/10 border-t border-base-200 grid grid-cols-2 gap-4">
                  <div class="space-y-4">
                     <div class="bg-base-100 p-3 rounded-xl border border-base-200">
                        <p class="text-[9px] font-black uppercase tracking-tighter opacity-40 mb-1">Payment Mode</p>
                        <p class="text-sm font-bold mb-3">{{ paymentModeLabel() }}</p>
                        <p class="text-[9px] font-black uppercase tracking-tighter opacity-40 mb-1">Payment Status</p>
                        <span class="text-xs font-black uppercase text-success" [class.text-error]="order()?.payment_status === 'PENDING'">{{ order()?.payment_status }}</span>
                        @if (order()?.current_status === 'DELIVERED' && order()?.payment_status === 'PENDING' && order()?.payment_method === 'cod') {
                           <button class="btn btn-xs mt-2 w-full btn-outline btn-success" (click)="showCodModal = true">Confirm COD Received</button>
                        }
                     </div>
                  </div>
                 <div class="space-y-3">
                    @if (order()?.canonical_bill) {
                       <app-order-bill-summary [canonicalBill]="order()!.canonical_bill" mode="full"></app-order-bill-summary>
                    } @else {
                       <p class="text-xs font-bold opacity-50 p-4 border border-dashed rounded-xl border-base-300">Legacy Bill Summary not available</p>
                    }
                 </div>
              </div>
           </div>

           <!-- Fulfillment Timeline -->
           <div class="card bg-base-100 border border-base-200 shadow-sm p-8">
              <h3 class="font-black text-sm uppercase tracking-widest opacity-60 mb-8">Order Lifecycle</h3>
              <div class="relative pl-8 space-y-8 before:absolute before:left-3 before:top-2 before:bottom-2 before:w-0.5 before:bg-base-200">
                 @for (history of order()?.status_history; track history.created_at) {
                    <div class="relative">
                       <div class="absolute -left-8 top-1 w-2.5 h-2.5 rounded-full bg-primary border-4 border-base-100"></div>
                       <div class="flex justify-between items-start">
                          <div>
                             <p class="text-xs font-black uppercase tracking-widest text-primary">{{ history.status }}</p>
                             <p class="text-[10px] opacity-40 font-bold mt-1">{{ history.note || 'Status updated by' }} {{ history.actor || 'System' }}</p>
                          </div>
                          <span class="text-[9px] font-mono opacity-40">{{ history.created_at | istDate:'shortTime' }}</span>
                       </div>
                    </div>
                 }
              </div>
           </div>
        </div>

        <!-- Sidebar Details -->
        <div class="space-y-6">
           @if (order()?.order_source === 'ADMIN_ASSISTED') {
              <div class="card bg-primary/5 border border-primary/20 shadow-sm p-6 text-primary">
                 <h3 class="font-black text-[10px] uppercase tracking-widest opacity-60 mb-4">Assisted Order Summary</h3>
                 <div class="space-y-3">
                    <div>
                       <p class="text-[9px] font-black opacity-60 uppercase tracking-tighter">Created By Admin UUID</p>
                       <p class="text-[11px] font-mono break-all">{{ order()?.created_by_admin || '-' }}</p>
                    </div>
                    @if (order()?.assisted_order_reason) {
                       <div>
                          <p class="text-[9px] font-black opacity-60 uppercase tracking-tighter">Reason</p>
                          <p class="text-xs font-bold">{{ order()?.assisted_order_reason }}</p>
                       </div>
                    }
                    @if (order()?.assisted_order_note) {
                       <div>
                          <p class="text-[9px] font-black opacity-60 uppercase tracking-tighter">Note</p>
                          <p class="text-xs font-bold">{{ order()?.assisted_order_note }}</p>
                       </div>
                    }
                 </div>
              </div>
           }

           <!-- Customer & Delivery Card -->
           <div class="card bg-base-100 border border-base-200 shadow-sm p-6">
              <h3 class="font-black text-[10px] uppercase tracking-widest opacity-40 mb-6">Delivery Assignment</h3>
              <div class="space-y-4">
                 <div class="flex items-center gap-4">
                    <div class="w-10 h-10 rounded-xl bg-base-200 flex items-center justify-center">
                       <i class="fa-solid fa-user opacity-30"></i>
                    </div>
                    <div>
                       <p class="text-xs font-black opacity-40 uppercase tracking-tighter">Recipient</p>
                       <p class="text-sm font-black">{{ order()?.customer?.receiver_name || 'Customer' }}</p>
                    </div>
                 </div>

                 <div class="flex items-center gap-4">
                    <div class="w-10 h-10 rounded-xl bg-error/10 text-error flex items-center justify-center" [class.bg-success/10]="order()?.phone_status === 'Valid'" [class.text-success]="order()?.phone_status === 'Valid'">
                       <i class="fa-solid fa-phone"></i>
                    </div>
                    <div>
                       <p class="text-xs font-black opacity-40 uppercase tracking-tighter">Contact Phone</p>
                       <p class="text-sm font-black">{{ order()?.delivery_phone }}</p>
                    </div>
                 </div>

                 <div class="flex items-start gap-4">
                    <div class="w-10 h-10 rounded-xl bg-error/10 text-error flex items-center justify-center shrink-0" [class.bg-info/10]="order()?.address_status === 'Valid'" [class.text-info]="order()?.address_status === 'Valid'">
                       <i class="fa-solid fa-location-dot"></i>
                    </div>
                    <div>
                       <p class="text-xs font-black opacity-40 uppercase tracking-tighter">Drop Address</p>
                       <p class="text-[11px] font-bold leading-relaxed mt-1" [class.text-error]="order()?.address_status !== 'Valid'">
                          {{ order()?.delivery_address }}
                       </p>
                    </div>
                 </div>
              </div>
              @if (deliveryMapUrl(); as mapUrl) {
                <a class="btn btn-sm btn-outline w-full rounded-xl mt-8" [href]="mapUrl" target="_blank" rel="noopener noreferrer">View Full Address Map</a>
              } @else {
                <p class="text-sm opacity-60 mt-8">Delivery address is unavailable for the map.</p>
              }
           </div>

           <!-- Rider Section -->
           <div class="card bg-base-100 border border-base-200 shadow-sm p-6 overflow-hidden relative">
              <div class="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -mr-16 -mt-16"></div>
              <h3 class="font-black text-[10px] uppercase tracking-widest opacity-40 mb-6">Logistics Partner</h3>
               @if (order()?.rider_id) {
                  <div class="flex items-center gap-3 bg-success/5 p-4 rounded-2xl border border-success/10 justify-between">
                     <div class="flex items-center gap-3">
                       @if (order()?.rider; as rider) {
                         @if (rider.profile_photo_url) {
                           <img class="h-14 w-14 rounded-xl object-cover shrink-0" [src]="rider.profile_photo_url" [alt]="rider.full_name || rider.name || 'Assigned rider'" (error)="rider.profile_photo_url = null">
                         } @else {
                           <span class="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center font-bold shrink-0">{{ (rider.full_name || rider.name || 'R')[0] }}</span>
                         }
                         <div class="min-w-0"><p class="font-bold break-words">{{rider.full_name || rider.name || 'Assigned rider'}}</p><p class="text-xs opacity-60">{{rider.code || 'No rider code'}}</p></div>
                       } @else {
                         <span class="text-xs font-black">Assigned rider details unavailable</span>
                       }
                     </div>
                     @if (order()?.current_status !== 'DISPATCHED' && order()?.current_status !== 'RIDER_NEAR_CUSTOMER' && order()?.current_status !== 'DELIVERED' && order()?.current_status !== 'CANCELLED') {
                        <button class="btn btn-xs btn-outline rounded-lg" (click)="showReassignModal = true">Change</button>
                     }
                  </div>
                  @if (order()?.rider; as rider) {
                    <dl class="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5 text-sm">
                      <div><dt class="opacity-60">Phone</dt><dd>@if (rider.account?.phone) { <a class="link link-primary" [href]="'tel:' + rider.account?.phone">{{rider.account?.phone}}</a> } @else { Not available }</dd></div>
                      <div><dt class="opacity-60">Email</dt><dd class="break-all">{{rider.account?.email || 'Not available'}}</dd></div>
                      <div><dt class="opacity-60">Vehicle</dt><dd>{{rider.vehicle_type || 'Not recorded'}}</dd></div>
                      <div><dt class="opacity-60">Verification</dt><dd>{{rider.verification_status || 'Not recorded'}}</dd></div>
                      <div><dt class="opacity-60">Status</dt><dd>{{rider.presence?.status || rider.status || 'Unknown'}}</dd></div>
                      <div><dt class="opacity-60">Availability</dt><dd>{{rider.presence ? (rider.presence.is_online ? 'Online' : 'Offline') : 'Unknown'}}</dd></div>
                    </dl>
                  }
               } @else {
                 <div class="text-center py-6 border-2 border-dashed border-base-200 rounded-2xl bg-base-200/20">
                    <p class="text-[10px] font-black uppercase opacity-30 mb-4">No Active Rider</p>
                    @if (order()?.current_status === 'READY_FOR_PICKUP') {
                      <a class="btn btn-xs btn-primary rounded-lg" routerLink="/orders">Choose rider in Orders Center</a>
                    } @else {
                      <p class="text-sm">Pick this order above. Rider search starts alongside packing.</p>
                    }
                 </div>
              }
         </div>
      </div>
      </div>
    </div>

    <!-- Modals -->
    @if (showCancelModal) {
      <div class="modal modal-open">
        <div class="modal-box">
          <h3 class="font-bold text-lg text-error">Cancel Order</h3>
          @if (order()?.payment_status === 'PAID') {
             <p class="text-sm mt-2 text-warning font-bold"><i class="fa-solid fa-triangle-exclamation mr-1"></i> This order is paid. A refund request will be created automatically.</p>
          }
          <div class="form-control mt-4">
             <label class="label"><span class="label-text font-bold">Reason</span></label>
             <select class="select select-bordered" [(ngModel)]="cancelReason">
                 <option value="" disabled>Select Reason</option>
                 <option value="CUSTOMER_REQUESTED">Customer Requested</option>
                 <option value="STORE_UNABLE_TO_FULFILL">Store Unable to Fulfill</option>
                 <option value="FRAUD">Fraud</option>
                 <option value="OTHER">Other</option>
             </select>
          </div>
          @if (['PREPARING', 'READY_FOR_PICKUP', 'RIDER_ASSIGNED', 'RIDER_ACCEPTED', 'RIDER_AT_STORE'].includes(order()?.current_status || '')) {
             <div class="form-control mt-4">
                <label class="label"><span class="label-text font-bold">Inventory Disposition</span></label>
                <select class="select select-bordered" [(ngModel)]="cancelDisposition">
                   <option value="RESTOCK">Restock all items</option>
                   <option value="WASTAGE">Record as wastage (Do not restock)</option>
                   <option value="NO_STOCK_CHANGE">No Stock Change</option>
                </select>
             </div>
          }
          <div class="form-control mt-4">
             <label class="label"><span class="label-text font-bold">Note (Optional)</span></label>
             <input type="text" class="input input-bordered" [(ngModel)]="cancelNote">
          </div>
          <div class="modal-action">
            <button class="btn" (click)="showCancelModal = false; cancelReason = ''; cancelNote = ''">Nevermind</button>
            <button class="btn btn-error text-white" [disabled]="!cancelReason" (click)="adminCancel()">Confirm Cancel</button>
          </div>
        </div>
      </div>
    }

    @if (showReportFailureModal) {
      <div class="modal modal-open">
        <div class="modal-box">
          <h3 class="font-bold text-lg text-warning">Report Delivery Failure</h3>
          <p class="text-xs opacity-60 mt-1">This will cancel the order and mark it pending return. Do not restock yet until goods physically arrive.</p>
          <div class="form-control mt-4">
             <label class="label"><span class="label-text font-bold">Reason</span></label>
             <select class="select select-bordered" [(ngModel)]="failureReason">
                 <option value="" disabled>Select Reason</option>
                 <option value="CUSTOMER_UNREACHABLE">Customer Unreachable</option>
                 <option value="CUSTOMER_REFUSED">Customer Refused</option>
                 <option value="WRONG_ADDRESS">Wrong Address</option>
                 <option value="RIDER_ISSUE">Rider Issue</option>
                 <option value="OTHER">Other</option>
             </select>
          </div>
          <div class="form-control mt-4">
             <label class="label"><span class="label-text font-bold">Note</span></label>
             <input type="text" class="input input-bordered" [(ngModel)]="failureNote">
          </div>
          <div class="modal-action">
            <button class="btn" (click)="showReportFailureModal = false; failureReason = ''; failureNote = ''">Nevermind</button>
            <button class="btn btn-warning text-white" [disabled]="!failureReason" (click)="reportFailure()">Report Failure</button>
          </div>
        </div>
      </div>
    }

    @if (showReturnModal) {
      <div class="modal modal-open">
        <div class="modal-box">
          <h3 class="font-bold text-lg text-info">Confirm Return to Store</h3>
          <div class="form-control mt-4">
             <label class="label"><span class="label-text font-bold">Inventory Disposition</span></label>
             <select class="select select-bordered" [(ngModel)]="returnDisposition">
                 <option value="RESTOCK">Restock</option>
                 <option value="WASTAGE">Wastage</option>
                 <option value="NO_STOCK_CHANGE">No Stock Change</option>
             </select>
          </div>
          <div class="form-control mt-4">
             <label class="label"><span class="label-text font-bold">Note</span></label>
             <input type="text" class="input input-bordered" [(ngModel)]="returnNote">
          </div>
          <div class="modal-action">
            <button class="btn" (click)="showReturnModal = false; returnNote = ''">Nevermind</button>
            <button class="btn btn-info text-white" (click)="confirmReturn()">Confirm Return</button>
          </div>
        </div>
      </div>
    }

    @if (showCodModal) {
      <div class="modal modal-open">
        <div class="modal-box">
          <h3 class="font-bold text-lg text-success">Confirm COD Received</h3>
          <div class="form-control mt-4">
             <label class="label"><span class="label-text font-bold">Verification Reason</span></label>
             <select class="select select-bordered" [(ngModel)]="codReason">
                 <option value="" disabled>Select...</option>
                 <option value="End-of-shift cash reconciliation">End-of-shift cash reconciliation</option>
                 <option value="Rider confirmed cash collected">Rider confirmed cash collected</option>
                 <option value="Cash accounted in store">Cash accounted in store</option>
                 <option value="Other">Other</option>
             </select>
          </div>
          <div class="modal-action">
            <button class="btn" (click)="showCodModal = false; codReason = ''">Nevermind</button>
            <button class="btn btn-success text-white" [disabled]="!codReason" (click)="confirmCod()">Confirm PAID</button>
          </div>
        </div>
      </div>
    }

    @if (showReassignModal) {
      <div class="modal modal-open">
        <div class="modal-box">
          <h3 class="font-bold text-lg">Change Rider</h3>
          <div class="form-control mt-4">
             <label class="label"><span class="label-text font-bold">Reason</span></label>
             <select class="select select-bordered" [(ngModel)]="reassignRiderReason">
                 <option value="" disabled>Select...</option>
                 <option value="RIDER_UNAVAILABLE">Rider Unavailable / Breakdown</option>
                 <option value="RIDER_REQUESTED">Rider Requested Removal</option>
                 <option value="VEHICLE_ISSUE">Vehicle Issue</option>
                 <option value="STORE_REQUESTED">Store Requested Change</option>
             </select>
          </div>
          <div class="modal-action">
            <button class="btn" (click)="showReassignModal = false; reassignRiderReason = ''">Nevermind</button>
            <button class="btn btn-primary" [disabled]="!reassignRiderReason" (click)="reassignRider()">Proceed</button>
          </div>
        </div>
      </div>
    }

    @if (showAdjustModal) {
      <div class="modal modal-open">
        <div class="modal-box">
          <h3 class="font-bold text-lg text-warning">Adjust Fulfillment</h3>
          <p class="text-xs mt-1">Item: {{ adjustingItem?.product_name }}</p>
          <div class="grid grid-cols-2 gap-4 mt-4">
             <div class="form-control">
                <label class="label"><span class="label-text font-bold">Ordered Qty</span></label>
                <div class="px-4 py-2 border rounded-xl bg-base-200/50 font-mono">{{ adjustingItem?.order_quantity }}</div>
             </div>
             <div class="form-control">
                <label class="label"><span class="label-text font-bold">Units to remove</span></label>
                <input type="number" class="input input-bordered w-full" min="1" [max]="(adjustingItem?.order_quantity || 0) - (adjustingItem?.cancelled_quantity || 0)" [(ngModel)]="adjustQuantity" (ngModelChange)="loadAdjustPreview()">
             </div>
          </div>

          @if (adjustPreview) {
             <div class="bg-base-200/50 p-4 rounded-xl mt-4 space-y-2 border border-base-300">
                <h4 class="font-black text-xs uppercase opacity-40">Financial Preview</h4>
                
                @if (adjustPreview.is_legacy_pricing) {
                    <p class="text-[10px] text-warning mb-2"><i class="fa-solid fa-triangle-exclamation"></i> Checkout pricing policy unavailable; original fees retained.</p>
                }

                <div class="flex justify-between text-xs"><span>Historical Unit Price</span><span>Rs {{ adjustPreview.historical_unit_price }}</span></div>
                <div class="flex justify-between text-xs"><span>Gross Line Reduction</span><span>Rs {{ adjustPreview.removed_merchandise }}</span></div>
                
                <div class="divider my-1"></div>
                
                @if (adjustPreview.new_delivery_fee > 0 || (order()?.pricing?.delivery_fee || 0) === 0) {
                    <div class="flex justify-between text-xs">
                        <span>Delivery Fee</span>
                        <span>
                            @if ((order()?.pricing?.delivery_fee || 0) === 0 && adjustPreview.new_delivery_fee > 0) {
                                <span class="line-through opacity-50 mr-1">₹0</span>
                                <span class="text-warning">Rs {{ adjustPreview.new_delivery_fee }}</span>
                            } @else {
                                Rs {{ adjustPreview.new_delivery_fee }}
                            }
                        </span>
                    </div>
                }
                
                @if (adjustPreview.new_small_order_fee > 0 || (order()?.pricing?.small_order_fee || 0) > 0) {
                    <div class="flex justify-between text-xs">
                        <span>Small Cart Fee</span>
                        <span>
                            @if ((order()?.pricing?.small_order_fee || 0) === 0 && adjustPreview.new_small_order_fee > 0) {
                                <span class="text-warning font-bold">Rs {{ adjustPreview.new_small_order_fee }}</span>
                            } @else {
                                Rs {{ adjustPreview.new_small_order_fee }}
                            }
                        </span>
                    </div>
                }

                <div class="flex justify-between text-xs font-bold pt-2 border-t border-base-300">
                   <span>New Payable</span><span class="text-primary">Rs {{ adjustPreview.new_payable }}</span>
                </div>
                @if (adjustPreview.savings_mrp_reliable && (adjustPreview.savings_mrp || 0) > 0) {
                   <div class="flex justify-between text-xs mt-1">
                      <span class="opacity-60">Savings on MRP</span><span class="text-success font-black">- Rs {{ adjustPreview.savings_mrp }}</span>
                   </div>
                }
                @if (adjustPreview.is_prepaid) {
                   <div class="flex justify-between text-xs font-bold text-success mt-1">
                      <span>Refund Due</span><span>Rs {{ adjustPreview.required_refund }}</span>
                   </div>
                } @else {
                   <div class="flex justify-between text-xs font-bold text-success mt-1">
                      <span>Amount to Collect</span><span>Rs {{ adjustPreview.new_payable }}</span>
                   </div>
                }
             </div>
          }

          <div class="form-control mt-4">
             <label class="label"><span class="label-text font-bold">Reason</span></label>
             <select class="select select-bordered" [(ngModel)]="adjustReason">
                 <option value="" disabled>Select Reason</option>
                 <option value="Out of stock">Out of stock</option>
                 <option value="Damaged item">Damaged item</option>
                 <option value="Customer requested">Customer requested</option>
             </select>
          </div>
          <div class="form-control mt-4">
             <label class="label"><span class="label-text font-bold">Inventory</span></label>
             <select class="select select-bordered" [(ngModel)]="adjustDisposition">
                 <option value="RESTOCK">Restock (Add to sellable stock)</option>
                 <option value="WASTAGE">Wastage (Do not restock)</option>
             </select>
          </div>
          <div class="modal-action">
            <button class="btn" (click)="closeAdjustModal()">Cancel</button>
            <button class="btn btn-warning text-white" [disabled]="!adjustReason || !adjustPreview" (click)="confirmAdjustment()">Adjust Units</button>
          </div>
        </div>
      </div>
    }
  `,
})
export class OrderDetailComponent implements OnInit {
  deliveryMapUrl() { return orderDeliveryMapUrl(this.order()); }
  readonly authService = inject(AuthService);
  private route = inject(ActivatedRoute);
  private ordersService = inject(OrdersService);
  private snackbar = inject(SnackbarService);
  private riderService = inject(RiderService);
  private pickingSlipService = inject(PickingSlipService);

  order = signal<AdminOrder | null>(null);
  printing = signal(false);
  fulfillmentBusy = signal(false);
  availableRiders = signal<Rider[]>([]);
  showRiderSelector = false;
  selectedRiderId: string | null = null;

  showPaymentMethodModal = false;

  getFinancialSummary = computed(() => {
    const o = this.order();
    
    if (o?.canonical_bill) {
      const cb = o.canonical_bill;
      return {
        originalItemsTotal: cb.merchandise.original,
        removedItemsTotal: cb.merchandise.removed,
        adjustedItemsTotal: cb.merchandise.fulfilled,
        finalPayable: cb.current_payable,
        deliveryFee: cb.fees.delivery.charged,
        packagingFee: cb.fees.packaging,
        platformFee: cb.fees.platform,
        smallOrderFee: cb.fees.small_order,
        coinUsed: cb.discounts.coins,
        discountAmount: cb.discounts.order_discount,
        deliveryDiscount: cb.fees.delivery.waiver,
        itemSavings: cb.savings.mrp_reliable ? cb.savings.mrp : 0
      };
    }

    let originalTotal = 0;
    let removedTotal = 0;
    
    if (o?.items) {
      o.items.forEach(item => {
         const qty = item.order_quantity || 0;
         const cancelled = item.cancelled_quantity || 0;
         const price = item.price || 0;
         
         originalTotal += (qty * price);
         removedTotal += (cancelled * price);
      });
    }
    
    return {
      originalItemsTotal: originalTotal,
      removedItemsTotal: removedTotal,
      adjustedItemsTotal: originalTotal - removedTotal,
      finalPayable: o?.payable_amount ?? o?.total_amount ?? 0,
      deliveryFee: o?.pricing?.delivery_fee || 0,
      packagingFee: o?.pricing?.packaging_fee || 0,
      platformFee: o?.pricing?.platform_fee || 0,
      smallOrderFee: o?.pricing?.small_order_fee || 0,
      coinUsed: o?.pricing?.coin_used || 0,
      discountAmount: o?.pricing?.discount_amount || 0,
      deliveryDiscount: o?.pricing?.delivery_discount || 0,
      itemSavings: 0 // Fallback does not compute this securely on admin UI currently
    };
  });


  showCancelModal = false;
  cancelReason = '';
  cancelNote = '';
  cancelDisposition: 'RESTOCK' | 'WASTAGE' | 'NO_STOCK_CHANGE' = 'RESTOCK';

  showReportFailureModal = false;
  failureReason = '';
  failureNote = '';

  showReturnModal = false;
  returnDisposition: 'RESTOCK' | 'WASTAGE' | 'NO_STOCK_CHANGE' = 'RESTOCK';
  returnNote = '';

  showCodModal = false;
  codReason = '';

  showReassignModal = false;
  reassignRiderReason = '';

  showAdjustModal = false;
  adjustingItem: any = null;
  adjustQuantity = 1;
  adjustReason = '';
  adjustNote = '';
  adjustDisposition: 'RESTOCK' | 'WASTAGE' | 'NO_STOCK_CHANGE' = 'RESTOCK';
  adjustPreview: any = null;

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.loadOrder(parseInt(id));
    }
    this.loadRiders();
  }

  loadOrder(id: number) {
    this.ordersService.getStoreAdminOrderDetails(id).subscribe({
      next: (res) => this.order.set(res),
      error: (err) => this.snackbar.error(err?.message || 'Could not fetch order details')
    });
  }

  loadRiders() {
    this.riderService.getAssignableRiders().subscribe({
      next: (res) => this.availableRiders.set(res),
      error: () => console.error('Failed to load riders')
    });
  }

  async reprintPickingSlip() {
    const activeOrder = this.order();
    if (!activeOrder) return;

    this.printing.set(true);
    try {
      const res = await this.pickingSlipService.reprint(activeOrder);
      if (res) {
        this.snackbar.success('Picking slip reprint triggered successfully');
        this.loadOrder(activeOrder.order_id);
      } else {
        this.snackbar.error('Failed to trigger reprint (Check console)');
      }
    } catch (err) {
      this.snackbar.error((err as Error)?.message || 'Error occurred during printing');
    } finally {
      this.printing.set(false);
    }
  }

  updateStatus(status: OrderStatus) {
    const order = this.order();
    if (!order) return;

    if (status === 'DELIVERED') {
      this.completeOrder();
      return;
    }

    this.ordersService.updateOrderStatus(order.order_id, status).subscribe({
      next: () => {
        this.snackbar.success(`Order #${order.order_id} status updated to ${status}`);
        this.loadOrder(order.order_id);

        if (status === 'PREPARING') {
           this.pickingSlipService.autoPrint(order).then(success => {
              if (!success) {
                 this.snackbar.error('Picking slip print failed / Retry manually');
              } else {
                 this.loadOrder(order.order_id);
              }
           });
        }
      },
      error: (err) =>
        this.snackbar.error(err?.message || 'Failed to update status'),
    });
  }

  showCompleteModal = false;
  completing = signal(false);
  completionMode: 'STORE_COLLECTION' | 'RIDER_DELIVERY' = 'STORE_COLLECTION';
  completionReason = '';
  completionCashConfirmed = false;
  completionReceived = false;
  completionHandover = false;
  private completionRequest: { id: string; payload: string } | null = null;

  canCompleteOrder(): boolean {
    const order = this.order();
    if (!order || !this.authService.hasRole(['superadmin', 'store_manager'])) return false;
    return order.rider_id
      ? ['RIDER_ASSIGNED', 'RIDER_ACCEPTED', 'RIDER_AT_STORE', 'DISPATCHED', 'RIDER_NEAR_CUSTOMER'].includes(order.current_status)
      : ['CONFIRMED', 'PREPARING', 'READY_FOR_PICKUP'].includes(order.current_status);
  }

  canConfirmCompletionCash(): boolean {
    return ['cod', 'cash'].includes(String(this.order()?.payment_method ?? '').toLowerCase());
  }

  paymentModeLabel(): string {
    const method = String(this.order()?.payment_method ?? '').trim().toLowerCase();
    if (['cod', 'cash', 'cash_on_delivery'].includes(method)) return 'COD (Cash on Delivery)';
    if (method === 'upi') return 'UPI';
    return method ? method.replace(/_/g, ' ').toUpperCase() : 'Not recorded';
  }

  canSubmitCompletion(): boolean {
    return !this.completing() && this.canCompleteOrder() && this.completionReceived &&
      this.completionItems().length > 0 && this.completionItems().every(item =>
        Number.isInteger(item.fulfilled_quantity) && item.fulfilled_quantity > 0) &&
      (this.completionMode !== 'RIDER_DELIVERY' || this.completionHandover) &&
      this.completionReason.trim().length <= 800 &&
      (this.order()?.payment_status?.toUpperCase() === 'PAID' ||
        (this.canConfirmCompletionCash() && this.completionCashConfirmed));
  }

  completionItems() {
    return this.order()?.items?.filter(item => item.fulfilled_quantity > 0) ?? [];
  }

  completeOrder() {
    if (!this.canCompleteOrder() || this.completing()) return;
    this.completionMode = this.order()?.rider_id ? 'RIDER_DELIVERY' : 'STORE_COLLECTION';
    this.completionReason = '';
    this.completionCashConfirmed = false;
    this.completionReceived = false;
    this.completionHandover = false;
    this.showCompleteModal = true;
  }

  submitCompletion() {
    const order = this.order();
    if (!order || !this.canSubmitCompletion()) return;
    const paymentAction = order.payment_status?.toUpperCase() === 'PAID' ? 'EXISTING_PAYMENT' : 'CASH_COLLECTED';
    const note = (this.completionMode === 'STORE_COLLECTION'
      ? 'Admin checked all listed quantities and confirmed customer store collection'
      : 'Admin checked all listed quantities and confirmed rider handover and customer receipt') +
      (this.completionReason.trim() ? `: ${this.completionReason.trim()}` : '');
    const receipt = { revision: order.fulfillment_revision ?? 0,
      items: this.completionItems().map(item => ({ item_id: item.id, quantity: item.fulfilled_quantity })),
      handoverConfirmed: this.completionHandover };
    const payload = JSON.stringify([order.order_id, this.completionMode, paymentAction, note, receipt]);
    if (this.completionRequest?.payload !== payload) {
      this.completionRequest = { id: crypto.randomUUID(), payload };
    }
    this.completing.set(true);
    this.ordersService.adminCompleteOrder(order.order_id, {
      requestId: this.completionRequest.id, mode: this.completionMode, paymentAction, note,
      receipt,
    }).subscribe({
      next: () => {
        this.completing.set(false);
        this.showCompleteModal = false;
        this.completionRequest = null;
        this.snackbar.success(`Order #${order.order_id} completed`);
        this.loadOrder(order.order_id);
      },
      error: (err) => {
        this.completing.set(false);
        this.snackbar.error(err?.message || 'Could not confirm completion. Retry to check the same request.');
      },
    });
  }

  canConfirmFulfillment(stage: 'PACK' | 'HANDOVER'): boolean {
    const order = this.order();
    if (!order || !this.authService.hasRole(['superadmin', 'store_manager'])) return false;
    return stage === 'PACK'
      ? !order.rider_id && ['CONFIRMED', 'PREPARING', 'READY_FOR_PICKUP'].includes(order.current_status || '')
      : !!order.rider_id && ['RIDER_ASSIGNED', 'RIDER_ACCEPTED', 'RIDER_AT_STORE'].includes(order.current_status || '');
  }

  async confirmFulfillment(stage: 'PACK' | 'HANDOVER') {
    const order = this.order();
    if (!order || this.fulfillmentBusy() || !this.canConfirmFulfillment(stage)) return;
    const items = this.completionItems().map(item => ({ item_id: item.id, quantity: item.fulfilled_quantity }));
    if (!items.length || items.some(item => !Number.isInteger(item.quantity) || item.quantity <= 0)) return;
    this.fulfillmentBusy.set(true);
    let packingSaved = false;
    try {
      await firstValueFrom(this.ordersService.confirmOrderFulfillment(order.order_id, {
        revision: order.fulfillment_revision ?? 0, items, stage, riderId: order.rider_id || null,
      }));
      if (stage === 'PACK') {
        packingSaved = true;
        let distance = { distanceKm: 2, estimatedMinutes: 15 };
        const route = this.ordersService.resolveOrderRoutePoints(order);
        if (route) {
          try {
            const res = await firstValueFrom(this.ordersService.getDistanceAndDuration(route.origin, route.destination));
            distance = { distanceKm: res.distanceMeters / 1000, estimatedMinutes: res.durationSeconds / 60 };
          } catch { distance = this.ordersService.estimateRouteFallback(route.origin, route.destination); }
        }
        await firstValueFrom(this.ordersService.markOrderReadyForPickup(order.order_id, distance));
      }
      this.snackbar.success(stage === 'PACK' ? 'Packing confirmed. Order ready for rider assignment.' : 'Handover confirmed. Rider can start delivery.');
      this.loadOrder(order.order_id);
    } catch (err) {
      this.snackbar.error((packingSaved ? 'Packing saved; ready/assignment failed. ' : '') +
        (err instanceof Error ? err.message : 'Could not save confirmation. Refresh and retry.'));
      this.loadOrder(order.order_id);
    } finally { this.fulfillmentBusy.set(false); }
  }

  getItemsTotal(): number {
    return (this.order()?.items || []).reduce(
      (sum, item) => sum + Number(item.total || 0),
      0
    );
  }

  getActiveLinesCount(): number {
    return (this.order()?.items || []).filter(item => !item.is_fully_removed).length;
  }

  getFulfilledQty(): number {
    return (this.order()?.items || []).reduce((sum, item) => sum + item.fulfilled_quantity, 0);
  }

  adminCancel() {
     const order = this.order();
     if (!order) return;
     this.ordersService.adminCancelOrder(order.order_id, {
        reason: this.cancelReason,
        note: this.cancelNote,
        disposition: (['PREPARING', 'READY_FOR_PICKUP', 'RIDER_ASSIGNED', 'RIDER_ACCEPTED', 'RIDER_AT_STORE'].includes(order.current_status || '')) ? this.cancelDisposition : 'RESTOCK'
     }).subscribe({
        next: () => {
           this.snackbar.success('Order cancelled successfully');
           this.showCancelModal = false;
           this.loadOrder(order.order_id);
        },
        error: (err) => this.snackbar.error(err?.message || 'Failed to cancel order')
     });
  }

  reportFailure() {
     const order = this.order();
     if (!order) return;
     this.ordersService.adminReportDeliveryFailure(order.order_id, {
        reason: this.failureReason,
        note: this.failureNote
     }).subscribe({
        next: () => {
           this.snackbar.success('Delivery failure reported');
           this.showReportFailureModal = false;
           this.loadOrder(order.order_id);
        },
        error: (err) => this.snackbar.error(err?.message || 'Failed to report delivery failure')
     });
  }

  confirmReturn() {
     const order = this.order();
     if (!order) return;
     this.ordersService.adminConfirmReturnToStore(order.order_id, {
        disposition: this.returnDisposition,
        note: this.returnNote
     }).subscribe({
        next: () => {
           this.snackbar.success('Return to store confirmed');
           this.showReturnModal = false;
           this.loadOrder(order.order_id);
        },
        error: (err) => this.snackbar.error(err?.message || 'Failed to confirm return')
     });
  }

  confirmCod() {
     const order = this.order();
     if (!order) return;
     this.ordersService.adminConfirmCodReceived(order.order_id, this.codReason).subscribe({
        next: () => {
           this.snackbar.success('COD Payment received and updated to PAID');
           this.showCodModal = false;
           this.loadOrder(order.order_id);
        },
        error: (err) => this.snackbar.error(err?.message || 'Failed to reconcile payment')
     });
  }

  reassignRider() {
     const order = this.order();
     if (!order) return;
     this.ordersService.reassignRider(order.order_id, this.reassignRiderReason).subscribe({
        next: () => {
           this.snackbar.success('Rider unassigned and put back into assignment queue');
           this.showReassignModal = false;
           this.loadOrder(order.order_id);
        },
        error: (err) => this.snackbar.error(err?.message || 'Failed to reassign rider')
     });
  }

   canAdjustFulfillment(): boolean {
      const order = this.order();
      if (!order) return false;
      const eligibleStatuses = ['PENDING', 'CONFIRMED', 'PREPARING', 'READY_FOR_PICKUP'];
      return eligibleStatuses.includes(String(order.current_status || '').toUpperCase());
   }

   getAdjustmentBlocker(): string | null {
      const order = this.order();
      if (!order) return 'Order not loaded';
      if ((order.pricing?.discount_amount || 0) > 0) return 'Partial adjustment is unavailable for discounted orders.';
      if ((order.pricing?.coin_used || 0) > 0) return 'Partial adjustment is unavailable for orders using coins.';
      if (order.items?.some(i => (i.price || 0) === 0)) return 'Partial adjustment is unavailable for orders with free/promotional items.';
      return null;
   }

  openAdjustModal(item: any) {
     const blocker = this.getAdjustmentBlocker();
     if (blocker) {
        this.snackbar.error(blocker);
        return;
     }
     this.adjustingItem = item;
     this.showAdjustModal = true;
     this.adjustQuantity = 1;
     this.adjustReason = '';
     this.adjustNote = '';
     this.adjustDisposition = 'RESTOCK';
     this.adjustPreview = null;
     this.loadAdjustPreview();
  }

  closeAdjustModal() {
     this.showAdjustModal = false;
     this.adjustingItem = null;
     this.adjustPreview = null;
  }

  loadAdjustPreview() {
     const order = this.order();
     if (!order || !this.adjustingItem || this.adjustQuantity <= 0) return;
     this.ordersService.previewOrderItemAdjustment(order.order_id, this.adjustingItem.id, this.adjustQuantity).subscribe({
        next: (preview) => this.adjustPreview = preview,
        error: (err) => {
           this.snackbar.error(err?.message || 'Failed to calculate preview');
           this.adjustPreview = null;
        }
     });
  }

  confirmAdjustment() {
     const order = this.order();
     if (!order || !this.adjustingItem) return;
     const idempotencyKey = crypto.randomUUID();
     this.ordersService.adminAdjustOrderItems(
        order.order_id,
        this.adjustingItem.id,
        this.adjustQuantity,
        this.adjustReason,
        this.adjustNote,
        this.adjustDisposition,
        idempotencyKey
     ).subscribe({
        next: () => {
           this.snackbar.success('Item adjusted successfully');
           this.closeAdjustModal();
           this.loadOrder(order.order_id);
        },
        error: (err) => this.snackbar.error(err?.message || 'Failed to adjust item')
     });
  }
}
